import { useState } from "react";
import { Head, router } from "@inertiajs/react";
import { BookOpen, Heart, Pencil, Trash2, TrendingUp, Undo2, Users, KeyRound, Download, RotateCcw } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import StatTile from "@/components/shared/StatTile";
import { DataTable, Th, Td, rowClass, EmptyRow } from "@/components/shared/DataTable";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { jsonRequest } from "@/lib/jsonRequest";
import { formatMoney, formatNumber, formatDateTime, pluralize } from "@/lib/format";

export default function Show({ school, membership, student, campaign, donations = [], minute_logs = [], parent_claims = null, activation, remove_path, back }) {
  const confirm = useConfirm();
  const [logs, setLogs] = useState(minute_logs);
  const minutesLogged = logs.reduce((sum, log) => sum + log.minutes, 0);

  const onRemove = async () => {
    const ok = await confirm({
      title: `Remove ${student.full_name}?`,
      body: "Their reading page, logged minutes, and family activations are deleted. This can't be undone.",
      confirmLabel: `Remove ${student.first_name}`,
      destructive: true,
    });
    if (ok) router.delete(remove_path);
  };

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      back={back}
      eyebrow={campaign.name}
      title={student.full_name}
      actions={remove_path && (
        <Button variant="outline" size="sm" onClick={onRemove} className="hover:text-destructive">
          <Trash2 className="h-4 w-4" /> Remove student
        </Button>
      )}
    >
      <Head title={`${student.full_name} · ${school.name}`} />

      <div className="space-y-10">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile icon={TrendingUp} label="Raised" value={formatMoney(student.raised_cents)} color="primary" />
          <StatTile icon={BookOpen} label="Minutes" value={formatNumber(minutesLogged)} color="forest" />
          <StatTile icon={Heart} label="Donations" value={formatNumber(donations.length)} />
          {activation && <ActivationTile activation={activation} firstName={student.first_name} />}
        </div>

        <ReadingLog
          logs={logs}
          totalMinutes={minutesLogged}
          onUpdate={(id, minutes) => setLogs((current) => current.map((log) => (log.id === id ? { ...log, minutes } : log)))}
          onRemove={(id) => setLogs((current) => current.filter((log) => log.id !== id))}
        />
        <Donations donations={donations} paidTotalCents={student.raised_cents} />
        {parent_claims && <Parents claims={parent_claims} firstName={student.first_name} canMakeNewCode={!!activation?.rotate_code_path} />}
      </div>
    </SchoolLayout>
  );
}

function SectionHeading({ icon: Icon, title, children }) {
  return (
    <div className="mb-3">
      <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-foreground">
        <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
        {title}
      </h2>
      {children && <p className="mt-1 text-sm text-muted-foreground">{children}</p>}
    </div>
  );
}

// The code on the student's take-home sheet, with a fresh sheet and (for
// school admins) a new code for a lost or misdirected one.
function ActivationTile({ activation, firstName }) {
  const confirm = useConfirm();

  const newCode = async () => {
    const ok = await confirm({
      title: `New activation code for ${firstName}?`,
      body: "The old code stops working. Families who already activated keep their access. Print a new take-home sheet afterwards.",
      confirmLabel: "Make a new code",
    });
    if (ok) router.post(activation.rotate_code_path, {}, { preserveScroll: true });
  };

  return (
    <div className="rounded-lg border border-border bg-background/60 px-4 py-3">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
        Activation code
      </p>
      <p className="mt-1 font-mono text-base font-medium text-foreground">{activation.claim_code}</p>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        <a href={activation.sheet_path} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
          <Download className="h-3 w-3" aria-hidden="true" /> Take-home sheet
        </a>
        {activation.rotate_code_path && (
          <button type="button" onClick={newCode} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
            <RotateCcw className="h-3 w-3" aria-hidden="true" /> New code
          </button>
        )}
      </div>
    </div>
  );
}

function ReadingLog({ logs, totalMinutes, onUpdate, onRemove }) {
  return (
    <section>
      <SectionHeading icon={BookOpen} title="Reading log">
        {pluralize(logs.length, "entry", "entries")} · {formatNumber(totalMinutes)} minutes
      </SectionHeading>
      <DataTable>
        <thead>
          <tr>
            <Th>Date</Th>
            <Th align="right">Minutes</Th>
            <Th>Logged by</Th>
            <Th>Note</Th>
            <Th align="right" srOnly>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0
            ? <EmptyRow colSpan={5}>No minutes logged yet.</EmptyRow>
            : logs.map((log) => <MinuteLogRow key={log.id} log={log} onUpdate={onUpdate} onRemove={onRemove} />)}
        </tbody>
      </DataTable>
    </section>
  );
}

const SOURCE_LABELS = { teacher_classroom: "class log", teacher_solo: "teacher", parent: "family" };

function MinuteLogRow({ log, onUpdate, onRemove }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(log.minutes));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputId = `log-minutes-${log.id}`;

  const save = async (e) => {
    e?.preventDefault();
    const next = parseInt(draft, 10);
    if (!Number.isFinite(next) || next < 1 || next > 1440) {
      setError("Enter between 1 and 1,440 minutes.");
      return;
    }
    if (next === log.minutes) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const result = await jsonRequest(log.edit_path, { method: "PATCH", data: { minutes: next } });
      onUpdate(log.id, result.minutes);
      setEditing(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const undo = async () => {
    const ok = await confirm({
      title: `Undo ${log.minutes} minutes?`,
      body: `Logged ${formatDateTime(log.logged_at)} by ${log.logged_by_name}. The minutes come off this student's total.`,
      confirmLabel: "Undo",
      destructive: true,
    });
    if (!ok) return;
    setSaving(true);
    setError("");
    try {
      await jsonRequest(log.undo_path, { method: "DELETE" });
      onRemove(log.id);
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  };

  return (
    <tr className={rowClass}>
      <Td className="whitespace-nowrap text-muted-foreground">{formatDateTime(log.logged_at)}</Td>
      <Td align="right">
        {editing ? (
          <form onSubmit={save} className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <label htmlFor={inputId} className="sr-only">Minutes</label>
              <input
                id={inputId}
                type="number"
                min="1"
                max="1440"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
                disabled={saving}
                aria-invalid={!!error}
                aria-describedby={error ? `${inputId}-error` : undefined}
                className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
              <span className="text-xs text-muted-foreground">min</span>
            </div>
            {error && <p id={`${inputId}-error`} className="text-xs text-destructive">{error}</p>}
          </form>
        ) : (
          <>
            <span className="font-semibold text-foreground">{formatNumber(log.minutes)}</span>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </>
        )}
      </Td>
      <Td className="text-foreground">
        {log.logged_by_name}
        <span className="text-muted-foreground"> · {SOURCE_LABELS[log.source] || log.source}</span>
      </Td>
      <Td className="max-w-xs truncate text-muted-foreground">{log.note || "—"}</Td>
      <Td align="right" className="whitespace-nowrap">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <Button type="button" size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setError(""); }} disabled={saving}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            {log.edit_path && (
              <Button type="button" size="sm" variant="ghost" onClick={() => { setDraft(String(log.minutes)); setEditing(true); }} disabled={saving} aria-label={`Edit ${log.minutes} minutes from ${formatDateTime(log.logged_at)}`}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )}
            {log.undo_path && (
              <Button type="button" size="sm" variant="ghost" onClick={undo} disabled={saving} className="hover:text-destructive" aria-label={`Undo ${log.minutes} minutes from ${formatDateTime(log.logged_at)}`}>
                <Undo2 className="h-4 w-4" /> {saving ? "Undoing…" : "Undo"}
              </Button>
            )}
          </div>
        )}
      </Td>
    </tr>
  );
}

function Donations({ donations, paidTotalCents }) {
  return (
    <section>
      <SectionHeading icon={Heart} title="Donations">
        {pluralize(donations.length, "donation")} · {formatMoney(paidTotalCents)} raised
      </SectionHeading>
      <DataTable>
        <thead>
          <tr>
            <Th>Date</Th>
            <Th>Donor</Th>
            <Th>Message</Th>
            <Th align="right">Amount</Th>
          </tr>
        </thead>
        <tbody>
          {donations.length === 0 ? (
            <EmptyRow colSpan={4}>No donations yet.</EmptyRow>
          ) : (
            donations.map((donation) => (
              <tr key={donation.id} className={rowClass}>
                <Td className="whitespace-nowrap text-muted-foreground">{formatDateTime(donation.paid_at || donation.created_at)}</Td>
                <Td>
                  <div className="font-medium text-foreground">{donation.donor_display_name}</div>
                  {donation.donor_email && <div className="text-xs text-muted-foreground">{donation.donor_email}</div>}
                </Td>
                <Td className="max-w-sm truncate text-muted-foreground">{donation.donor_message ? `“${donation.donor_message}”` : "—"}</Td>
                <Td align="right" className="text-foreground">{formatMoney(donation.amount_cents, { cents: true })}</Td>
              </tr>
            ))
          )}
        </tbody>
      </DataTable>
    </section>
  );
}

// School-admin only: who has activated this student's page. Removing a
// link cuts that person's access right away.
function Parents({ claims, firstName, canMakeNewCode }) {
  const confirm = useConfirm();

  const onRemove = async (claim) => {
    const ok = await confirm({
      title: `Remove ${claim.email}?`,
      body: `They lose access to ${firstName}'s page right away. Minutes they logged stay.`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) router.delete(claim.remove_path, { preserveScroll: true });
  };

  return (
    <section>
      <SectionHeading icon={Users} title="Families">
        Everyone who activated {firstName}'s page.
        {canMakeNewCode && " If the activation code reached the wrong person, remove them and make a new code (above)."}
      </SectionHeading>
      <DataTable>
        <thead>
          <tr>
            <Th>Email</Th>
            <Th>Activated</Th>
            <Th srOnly>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {claims.length === 0 ? (
            <EmptyRow colSpan={3}>No one has activated {firstName}'s page yet.</EmptyRow>
          ) : (
            claims.map((claim) => (
              <tr key={claim.id} className={rowClass}>
                <Td className="font-medium text-foreground">{claim.email}</Td>
                <Td className="whitespace-nowrap text-muted-foreground">{formatDateTime(claim.linked_at)}</Td>
                <Td align="right">
                  <Button type="button" size="sm" variant="ghost" onClick={() => onRemove(claim)} className="hover:text-destructive" aria-label={`Remove ${claim.email}`}>
                    <Trash2 className="h-4 w-4" /> Remove
                  </Button>
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </DataTable>
    </section>
  );
}
